import axios from 'axios';

export const CANONICAL_HOME_REQUEST_URL =
  '/api/home?horizon=daily&limit=50&rankingMode=closest_realized';

const CANONICAL_HOME_REQUEST_HEADERS = Object.freeze({
  'Cache-Control': 'no-cache, no-store, max-age=0',
  Pragma: 'no-cache',
});

let acceptedHomeBundle;
let bundledMarketAnalysisState = null;
let homeRequestGeneration = 0;
let inFlightHomeRequest = null;

const isObject = (value) => (
  Boolean(value) && typeof value === 'object' && !Array.isArray(value)
);

const normalizedDate = (value) => {
  const raw = String(value || '').trim();
  const candidate = raw.slice(0, 10);
  return /^\d{4}-\d{2}-\d{2}$/.test(candidate) ? candidate : null;
};

const normalizedStatus = (value) => String(value || '').trim().toLowerCase();

const textList = (value) => (
  Array.isArray(value)
    ? value.map((item) => String(item || '').trim()).filter(Boolean)
    : []
);

const isFiniteHomeNumber = (value) => {
  if (value === null || value === undefined || String(value).trim() === '') {
    return false;
  }
  return Number.isFinite(Number(value));
};

const isUsableRanking = (value) => {
  const status = normalizedStatus(value?.status);
  return (
    isObject(value) &&
    isObject(value.meta) &&
    Array.isArray(value.items) &&
    status !== 'error' &&
    status !== 'unavailable'
  );
};

const hasRequiredHomeData = (bundle) => {
  const rankings = Array.isArray(bundle?.top50Rankings)
    ? bundle.top50Rankings
    : (isObject(bundle?.top50Rankings) ? Object.values(bundle.top50Rankings) : []);
  return (
    isFiniteHomeNumber(bundle?.deepIndex) &&
    isObject(bundle?.dowChart) &&
    isObject(bundle.dowChart.info) &&
    Array.isArray(bundle.dowChart.close) &&
    bundle.dowChart.close.length > 0 &&
    isUsableRanking(bundle?.top50) &&
    rankings.length >= 3 &&
    rankings.every(isUsableRanking)
  );
};

const projectBundledGeneral = (bundle) => {
  const general = bundle?.marketOverview?.marketAnalysis?.general;
  if (!isObject(general)) {
    return {kind: 'unavailable'};
  }

  const currentStatus = normalizedStatus(general.currentStatus || general.status);
  const status = normalizedStatus(general.status);
  const expectedRunDate = normalizedDate(general.expectedRunDate);
  const contentAsOf = normalizedDate(general.contentAsOf);
  const terminal = currentStatus === 'error' || status === 'error';

  if (terminal) {
    return {
      kind: 'terminal',
      currentStatus: 'error',
      expectedRunDate,
      contentAsOf: null,
      isFallback: false,
      analysis: null,
      reason: general.reason || 'current_run_failed',
    };
  }

  const subject = String(general.subject || '').trim();
  const brief = String(general.brief || '').trim();
  const bullets = textList(general.bullets);
  const paragraphs = textList(general.paragraphs);
  const renderable = Boolean(subject || brief || bullets.length || paragraphs.length);
  const analysis = {
    subject,
    brief,
    bullets,
    paragraphs,
    sentimentLabel: general.sentimentLabel,
    sentimentScore: general.sentimentScore,
  };
  const sameExpectedDate = !expectedRunDate || expectedRunDate === contentAsOf;
  const isCurrentLiveContent = (
    (status === 'live' || status === 'ok') &&
    (currentStatus === 'ok' || currentStatus === 'live') &&
    general.isFallback !== true &&
    Boolean(contentAsOf) &&
    sameExpectedDate &&
    renderable
  );
  if (isCurrentLiveContent) {
    return {
      kind: 'usable',
      currentStatus,
      expectedRunDate,
      contentAsOf,
      isFallback: false,
      analysis,
    };
  }

  const fallbackStatus = ['pending', 'missing', 'retrying'].includes(currentStatus);
  const isUsableFallback = (
    status === 'fallback' &&
    general.isFallback === true &&
    fallbackStatus &&
    Boolean(expectedRunDate) &&
    Boolean(contentAsOf) &&
    renderable
  );
  if (isUsableFallback) {
    return {
      kind: 'fallback',
      currentStatus,
      expectedRunDate,
      contentAsOf,
      isFallback: true,
      analysis,
    };
  }

  return {
    kind: 'unavailable',
    currentStatus,
    expectedRunDate,
    contentAsOf,
    isFallback: general.isFallback === true,
  };
};

const unwrapCanonicalHomeEnvelope = (response) => {
  const envelope = response?.data;
  if (envelope?.success !== true || !isObject(envelope.data)) {
    const message = envelope?.message || envelope?.error?.message || 'Failed to load Home data.';
    throw new Error(message);
  }
  if (!hasRequiredHomeData(envelope.data)) {
    throw new Error('Home response did not contain the required chart and ranking data.');
  }
  return envelope.data;
};

const publishAcceptedHomeBundle = (bundle, generation) => {
  if (generation !== homeRequestGeneration) {
    if (acceptedHomeBundle) {
      return acceptedHomeBundle;
    }
    if (inFlightHomeRequest?.generation > generation) {
      return inFlightHomeRequest.promise;
    }
    throw new Error('Home request was superseded.');
  }
  acceptedHomeBundle = bundle;
  bundledMarketAnalysisState = projectBundledGeneral(bundle);
  return bundle;
};

export const getAcceptedHomeBundle = () => acceptedHomeBundle;

export const getBundledMarketAnalysisGeneral = () => (
  ['usable', 'fallback', 'terminal'].includes(bundledMarketAnalysisState?.kind)
    ? bundledMarketAnalysisState
    : null
);

export const getBundledMarketAnalysisState = () => bundledMarketAnalysisState;

export const resolveBundledMarketAnalysisState = (state, runDate) => {
  if (!state || !['usable', 'fallback', 'terminal'].includes(state.kind)) {
    return null;
  }
  const requestedDate = normalizedDate(runDate);
  if (!requestedDate) {
    return state;
  }
  if (state.kind === 'usable') {
    return state.contentAsOf === requestedDate ? state : null;
  }
  return !state.expectedRunDate || state.expectedRunDate === requestedDate
    ? state
    : null;
};

export const shouldRequestMarketAnalysisGeneral = (state, runDate) => (
  !resolveBundledMarketAnalysisState(state, runDate)
);

export const loadCanonicalHomeBundle = ({
  force = false,
  request = axios.request,
} = {}) => {
  if (!force && acceptedHomeBundle) {
    return Promise.resolve(acceptedHomeBundle);
  }
  if (!force && inFlightHomeRequest) {
    return inFlightHomeRequest.promise;
  }

  const generation = ++homeRequestGeneration;
  const promise = Promise.resolve()
    .then(() => request({
      method: 'GET',
      url: CANONICAL_HOME_REQUEST_URL,
      headers: {...CANONICAL_HOME_REQUEST_HEADERS},
    }))
    .then(unwrapCanonicalHomeEnvelope)
    .then((bundle) => publishAcceptedHomeBundle(bundle, generation))
    .catch((error) => {
      if (generation === homeRequestGeneration && acceptedHomeBundle) {
        return acceptedHomeBundle;
      }
      throw error;
    })
    .finally(() => {
      if (inFlightHomeRequest?.generation === generation) {
        inFlightHomeRequest = null;
      }
    });

  inFlightHomeRequest = {generation, promise};
  return promise;
};

export const resetCanonicalHomeBundleForTests = () => {
  acceptedHomeBundle = undefined;
  bundledMarketAnalysisState = null;
  homeRequestGeneration = 0;
  inFlightHomeRequest = null;
};
